import React, { useState, useEffect, useRef, useContext } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import moment from 'moment';
import { FaBell, FaVideo, FaClock, FaCalendarCheck } from 'react-icons/fa';
import { UserContext } from '../../context/UserContext';
import { API } from '../../config';
import styles from './NotificationBell.module.css';

const POLL_INTERVAL_MS = 60000; // refresh every minute

const NotificationBell = () => {
    const user = useContext(UserContext);
    const [notifications, setNotifications] = useState([]);
    const [open, setOpen] = useState(false);
    const [loading, setLoading] = useState(false);
    const wrapperRef = useRef(null);

    const fetchNotifications = async () => {
        if (!user || !user.email) return;
        setLoading(true);
        try {
            const response = await fetch(
                `${API.GET_NOTIFICATIONS_ENDPOINT}?email=${encodeURIComponent(user.email)}&role=${encodeURIComponent(user.role || '')}`
            );
            const data = await response.json();
            if (data.success) {
                setNotifications(data.notifications || []);
            }
        } catch (err) {
            console.error('Failed to fetch notifications:', err);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchNotifications();
        const interval = setInterval(fetchNotifications, POLL_INTERVAL_MS);
        return () => clearInterval(interval);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [user?.email]);

    useEffect(() => {
        const handleClickOutside = (e) => {
            if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
                setOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    if (!user || !user.email) return null;

    const count = notifications.length;

    return (
        <div className={styles.wrapper} ref={wrapperRef}>
            <button
                className={styles.bellButton}
                onClick={() => setOpen(o => !o)}
                aria-label={`Notifications${count > 0 ? ` (${count} unread)` : ''}`}
            >
                <FaBell />
                {count > 0 && (
                    <motion.span
                        className={styles.badge}
                        initial={{ scale: 0 }}
                        animate={{ scale: 1 }}
                        transition={{ type: 'spring', stiffness: 500, damping: 20 }}
                    >
                        {count > 9 ? '9+' : count}
                    </motion.span>
                )}
            </button>

            <AnimatePresence>
                {open && (
                    <motion.div
                        className={styles.dropdown}
                        initial={{ opacity: 0, y: -8, scale: 0.97 }}
                        animate={{ opacity: 1, y: 0, scale: 1 }}
                        exit={{ opacity: 0, y: -8, scale: 0.97 }}
                        transition={{ duration: 0.18, ease: 'easeOut' }}
                    >
                        <div className={styles.dropdownHeader}>
                            <h4>Notifications</h4>
                            {loading && <span className={styles.loadingDot} />}
                        </div>

                        {notifications.length === 0 ? (
                            <div className={styles.emptyState}>
                                <FaCalendarCheck />
                                <p>You're all caught up.</p>
                            </div>
                        ) : (
                            <ul className={styles.list}>
                                {notifications.map((n, i) => (
                                    <motion.li
                                        key={`${n.type}-${n.meetingKey}-${i}`}
                                        className={styles.item}
                                        initial={{ opacity: 0, x: -8 }}
                                        animate={{ opacity: 1, x: 0 }}
                                        transition={{ delay: i * 0.03 }}
                                    >
                                        <span className={`${styles.icon} ${n.type === 'recording' ? styles.iconRecording : styles.iconUpcoming}`}>
                                            {n.type === 'recording' ? <FaVideo /> : <FaClock />}
                                        </span>
                                        <div className={styles.itemBody}>
                                            <p className={styles.itemTitle}>{n.topic}</p>
                                            {n.type === 'upcoming' ? (
                                                <p className={styles.itemMeta}>
                                                    Starts {moment(n.startTime).fromNow()}
                                                </p>
                                            ) : (
                                                <p className={styles.itemMeta}>Recording ready to watch</p>
                                            )}
                                        </div>
                                        {n.type === 'upcoming' ? (
                                            <a href={n.joinLink} target="_blank" rel="noopener noreferrer" className={styles.itemAction}>
                                                Join
                                            </a>
                                        ) : (
                                            <a href={n.playUrl} target="_blank" rel="noopener noreferrer" className={styles.itemAction}>
                                                Watch
                                            </a>
                                        )}
                                    </motion.li>
                                ))}
                            </ul>
                        )}
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    );
};

export default NotificationBell;
